import React, { useState } from 'react';
import { Check, Star, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const Offers = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [openFaq, setOpenFaq] = useState(null);

  const offersData = t.offers || {};
  const packs = offersData.packs || {};

  // Order: Chance Immédiate, Starter, Premium (popular), VIP Max - same as probawin.com
  const packsList = [
    { key: 'chance', ...packs.chance },
    { key: 'starter', ...packs.starter },
    { key: 'premium', ...packs.premium },
    { key: 'vip', ...packs.vip },
  ].filter((p) => p.name);

  const handleSelectOffer = (packKey, packName) => {
    toast({
      title: `${packName}`,
      description:
        'Cette fonctionnalité sera bientôt disponible (paiement en cours d\'intégration).',
    });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="offers-page">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {offersData.heroTitle}
          </h1>
          <p className="text-base md:text-lg font-semibold text-gray-800 mb-4">
            {offersData.heroSubtitle}
          </p>
          <p className="text-base md:text-lg text-gray-600">
            {offersData.heroDescription}
          </p>
        </div>
      </section>

      {/* Offers Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packsList.map((pack) => {
              const isPopular = !!pack.popular;
              return (
                <Card
                  key={pack.key}
                  className={`relative flex flex-col hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${
                    isPopular ? 'border-emerald-500 border-2 shadow-xl' : ''
                  }`}
                  data-testid={`pack-${pack.key}`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg whitespace-nowrap">
                        <Star className="w-3.5 h-3.5" fill="currentColor" />
                        {offersData.bestSeller}
                      </div>
                    </div>
                  )}

                  <CardHeader className="text-center pb-4 pt-8">
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {pack.name}
                    </CardTitle>
                    <p className="text-sm text-gray-600 mb-4 min-h-[40px]">
                      {pack.description}
                    </p>
                    <div className="mt-2">
                      <span className="text-4xl font-bold text-gray-900">
                        €{pack.price}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        {pack.period === 'one_time'
                          ? offersData.periodOneTime
                          : offersData.periodMonthly}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex flex-col flex-1 pb-6">
                    <ul className="space-y-3 mb-6 flex-1">
                      {(pack.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => handleSelectOffer(pack.key, pack.name)}
                      data-testid={`btn-pack-${pack.key}`}
                      className={`w-full py-5 text-sm font-semibold transition-all duration-300 hover:scale-[1.02] ${
                        isPopular
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                    >
                      {pack.cta}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose ProbaWins Club */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {offersData.whyTitle}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {offersData.whySubtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(offersData.whyItems || []).map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold mb-4">
                    {idx + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {offersData.faqTitle}
            </h2>
            <p className="text-base text-gray-600">{offersData.faqSubtitle}</p>
          </div>
          <div className="space-y-3">
            {(offersData.faq || []).map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <Card
                  key={idx}
                  className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  data-testid={`faq-item-${idx}`}
                >
                  <CardContent className="p-0">
                    <button
                      className="w-full flex items-center justify-between p-5 text-left"
                      type="button"
                    >
                      <span className="font-semibold text-gray-900 pr-4">
                        {item.q}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-sm text-gray-700 leading-relaxed">
                        {item.a}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Offers;
